import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { usePushReportsRecipientForUserQuery } from "org/user/queries/usePushReportsRecipientForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { useRemovePushReportRecipientUtils } from "./useRemovePushReportRecipientUtils";
import { useReplacePushReportRecipientUtils } from "./useReplacePushReportRecipientUtils";
import {
    PushReportRecipientRow,
    useUserPushReportsRecipientListTable,
} from "./useUserPushReportsRecipientListTable";

export function UserPushReportsRecipientList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { data } = usePushReportsRecipientForUserQuery(userId);
    const { t } = useTranslation();
    const { table, tableId, getFilterComponent } =
        useUserPushReportsRecipientListTable(data);
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeUserToolState,
        isRemoveUserModalOpen,
        openRemoveUserModal,
        closeRemoveUserModal,
        removeUser,
        isRemovingUser,
    } = useRemovePushReportRecipientUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplacePushReportRecipientUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveUserModal}
                onSubmit={() => void removeUser()}
                open={isRemoveUserModalOpen}
                loading={isRemovingUser}
                title={t("User_PushReports_Recipient_RemoveUser")}
                message={t("User_PushReports_Recipient_RemoveUser_DialogText", {
                    user: formatUserName(user),
                    count: selectedRows.length,
                })}
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={(userOrGroup) => void replaceUser(userOrGroup)}
                user={user}
            />
            <AssignmentTable<PushReportRecipientRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                modeConfig={getFilterModeConfig(t)}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeUserToolState}
                            onClick={openRemoveUserModal}
                        >
                            {t("User_PushReports_Recipient_RemoveUser")}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
